/** Handball live match clock helpers (regular + OT + seven-metre shootout). */

export const MATCH_DURATION_OPTIONS = [10, 20, 30, 40, 50, 60] as const;
export type MatchDurationMinutes = (typeof MATCH_DURATION_OPTIONS)[number];

export const OT_HALF_MINUTES = 5;

export const LIVE_PERIODS = [
  '1H',
  '2H',
  'OT1_1H',
  'OT1_2H',
  'OT2_1H',
  'OT2_2H',
  'S7M',
] as const;

export type LivePeriod = (typeof LIVE_PERIODS)[number];

export const LIVE_PERIOD_LABELS: Record<LivePeriod, string> = {
  '1H': '上半場',
  '2H': '下半場',
  OT1_1H: '加時一（上）',
  OT1_2H: '加時一（下）',
  OT2_1H: '加時二（上）',
  OT2_2H: '加時二（下）',
  S7M: '七米球決勝',
};

export const LIVE_STATUS_LABELS: Record<string, string> = {
  ready: '待開始',
  live: '進行中',
  paused: '暫停',
  halftime: '中場休息',
  ended: '已結束',
};

export function isClockRunning(session: {
  status: string;
  clock_started_at?: string | null;
}): boolean {
  return session.status === 'live' && !!session.clock_started_at;
}

/** Seconds cap for the current period segment (null = no auto-stop). */
export function getPeriodCapSeconds(period: string, matchDurationMinutes: number): number | null {
  const half = (matchDurationMinutes * 60) / 2;
  if (period === '1H' || period === '2H') return half;
  if (period.startsWith('OT')) return OT_HALF_MINUTES * 60;
  if (period === 'S7M') return null;
  return null;
}

export function parseClockInput(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (trimmed.includes(':')) {
    const [m, s] = trimmed.split(':');
    const mins = parseInt(m, 10);
    const secs = parseInt(s, 10);
    if (Number.isFinite(mins) && Number.isFinite(secs) && secs >= 0 && secs < 60) {
      return mins * 60 + secs;
    }
    return null;
  }
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function formatClockInput(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** True when the current period segment has reached its cap and the clock is not running. */
export function isPeriodSegmentComplete(
  period: string,
  matchDurationMinutes: number,
  currentSeconds: number,
  running: boolean
): boolean {
  if (running) return false;
  const cap = getPeriodCapSeconds(period, matchDurationMinutes);
  if (cap == null) return false;
  return currentSeconds >= cap;
}

export const OT_FLOW_HINT =
  '常規時間賽和：休息 5 分鐘 → 加時一（上下半場各 5 分鐘，中場 1 分鐘）→ 仍和則休息 5 分鐘 → 加時二（同上）→ 仍和則按賽規（可進行七米球決勝）。';
